import React from "react";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { FormProvider } from "react-hook-form";
import { Link } from "react-router-dom";
import type { UseFormReturn } from "react-hook-form";
import mappedIcon from "assets/images/mappedicon.svg";
import { RHFSelect } from "components/RHF";
import type {
  StoreAuditSummaryRangeDayModel,
  StoreBackstockLedgerModel,
  StoreScratcherReconciliationAuditsRangeModel,
} from "models";
import { auditDashboardTheme, auditToneStyles, getAuditTone } from "../../theme";
import { buildDayInProgressMetricRows, isAuditDayInProgress } from "../../utils/auditDayProgress";
import { getAuditDataAvailability } from "../../utils/auditMissingState";
import type { AuditListFilterKey, AuditListFilterOption } from "../../utils/auditListFilters";
import type { AuditMetricRowData } from "../../types/auditMetrics";
import AuditSummaryComparisonTable from "../shared/AuditSummaryComparisonTable";
import AuditDashboardBackstockLink from "../shared/AuditDashboardBackstockLink";
import type { ManualSoldOutImpact, ManualSoldOutReport } from "services/scratcherService";

interface AuditDailyAuditGridProps {
  storeId?: string;
  days: StoreAuditSummaryRangeDayModel[];
  selectedReportDate: string | null;
  sortDirection: "asc" | "desc";
  onSortDirectionChange: React.Dispatch<React.SetStateAction<"asc" | "desc">>;
  onSelectReportDate: (reportDate: string) => void;
  hasPosType: boolean;
  isLottoAuthorized: boolean;
  auditFilterOptions: AuditListFilterOption[];
  filterFormMethods: UseFormReturn<{ auditListFilter: AuditListFilterKey }>;
  onAuditFilterChange: (filter: AuditListFilterKey) => void;
  scratcherAuditWindows?: StoreScratcherReconciliationAuditsRangeModel | null;
  backstockLedger?: StoreBackstockLedgerModel | null;
  manualSoldOutReportsByDate?: Map<string, ManualSoldOutReport>;
  manualSoldOutImpactsByDate?: Map<string, ManualSoldOutImpact>;
  onVisibleReportDatesChange?: (reportDates: string[]) => void;
}

// Kept for the commented legacy grid below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dailyGridColumns =
  "minmax(96px, 1.25fr) minmax(82px, 0.95fr) minmax(54px, 0.62fr) minmax(58px, 0.68fr) minmax(62px, 0.78fr)";

const formatSignedCurrency = (value: number) => {
  const amount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));

  return `${value > 0 ? "+" : "-"}${amount}`;
};

const formatCurrency = (value: number | null | undefined) =>
  value == null
    ? "-"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

const formatNumber = (value: number | null | undefined) => (value == null ? "-" : value.toLocaleString("en-US"));

const pluralize = (value: number, singular: string, plural: string) => `${formatNumber(value)} ${value === 1 ? singular : plural}`;

const formatVarianceNumber = (value: number | null | undefined) => {
  if (value == null) {
    return "-";
  }

  return value === 0 ? 0 : formatNumber(value);
};

const formatVarianceCurrency = (value: number | null | undefined) => {
  if (value == null) {
    return "-";
  }

  return value === 0 ? 0 : formatCurrency(value);
};

type DailyGridMetricValue =
  | { kind: "empty" }
  | { kind: "matched" }
  | { kind: "variance"; value: number }
  | { kind: "missing" }
  | { kind: "needs"; missing: string[] }
  | { kind: "inProgress" };

const toGridVarianceValue = (value: number | null | undefined): DailyGridMetricValue => {
  if (value == null) {
    return { kind: "empty" };
  }

  if (value === 0) {
    return { kind: "matched" };
  }

  return { kind: "variance", value };
};

// Kept for the commented legacy grid below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const renderGridVarianceValue = (metric: DailyGridMetricValue) => {
  if (metric.kind === "empty") {
    return (
      <Typography component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 13, fontWeight: 700, lineHeight: 1.1 }}>
        -
      </Typography>
    );
  }

  if (metric.kind === "missing") {
    return (
      <Typography component="span" sx={{ color: auditDashboardTheme.colors.textNegative, fontSize: 13, fontWeight: 600, lineHeight: 1.1 }}>
        Missing
      </Typography>
    );
  }

  if (metric.kind === "inProgress") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-grid",
          justifyItems: "end",
          rowGap: 0,
          color: auditDashboardTheme.colors.textProgress,
        }}
      >
        {["Day In", "Progress"].map((line) => (
          <Typography
            key={line}
            component="span"
            sx={{
              color: "inherit",
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.05,
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    );
  }

  if (metric.kind === "needs") {
    return (
      <Box sx={{ display: "grid", justifyItems: "end", gap: "2px", width: "100%", minWidth: 0 }}>
        <Typography component="span" sx={{ color: auditDashboardTheme.colors.textNegative, fontSize: 13, fontWeight: 600, lineHeight: 1.05 }}>
          Needs
        </Typography>
        <Typography
          component="span"
          sx={{
            color: auditDashboardTheme.colors.textMutedStrong,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.05,
            whiteSpace: "normal",
            overflowWrap: "break-word",
            wordBreak: "normal",
            textAlign: "right",
            maxWidth: "100%",
          }}
        >
          {metric.missing.join(" + ")}
        </Typography>
      </Box>
    );
  }

  if (metric.kind === "matched") {
    return (
      <Box
        component="img"
        src={mappedIcon}
        alt="Matched"
        sx={{
          display: "inline-block",
          width: 18,
          height: 18,
          verticalAlign: "middle",
        }}
      />
    );
  }

  return (
    <Typography
      component="span"
      sx={{
        color: metric.value > 0 ? auditDashboardTheme.colors.textPositive : auditDashboardTheme.colors.textNegative,
        fontSize: "clamp(11px, 2.45vw, 13px)",
        fontWeight: 600,
        lineHeight: 1.1,
        fontVariantNumeric: "tabular-nums",
        display: "inline-block",
        maxWidth: "100%",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        textAlign: "right",
      }}
    >
      {formatSignedCurrency(metric.value)}
    </Typography>
  );
};

// Kept for the commented legacy grid below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const metricCellSx = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-end",
  alignSelf: "stretch",
  minWidth: 0,
  pt: "1px",
  textAlign: "right",
} as const;

// Kept for the commented legacy grid below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getDailyAuditGridValues = (
  day: StoreAuditSummaryRangeDayModel,
  options: { hasPosType: boolean; isLottoAuthorized: boolean }
) => {
  const totals = day.auditTotals;
  const isDayInProgress = isAuditDayInProgress(day.reportDate);
  const variance = totals?.reconciliation_variance;
  const availability = getAuditDataAvailability(day, { ...options, isDayInProgress });

  return {
    scratcher: toGridVarianceValue(availability.canShowScratcherVariance ? variance?.scratcher?.revenue_variance : null),
    lotto:
      !isDayInProgress && options.isLottoAuthorized && availability.isLottoMissing
        ? { kind: "missing" as const }
        : toGridVarianceValue(availability.canShowLottoVariance ? variance?.lotto_sales_variance : null),
    payouts: toGridVarianceValue(availability.canShowLottoVariance ? variance?.payout_variance : null),
    overall:
      isDayInProgress
        ? { kind: "inProgress" as const }
        : availability.hasMissingSources
          ? { kind: "needs" as const, missing: availability.missingSources }
          : toGridVarianceValue(availability.canShowOverallVariance ? variance?.total_variance : null),
  };
};

const buildDailyAuditRows = (
  day: StoreAuditSummaryRangeDayModel,
  options: { hasPosType: boolean; isLottoAuthorized: boolean }
): AuditMetricRowData[] => {
  const totals = day.auditTotals;
  const isDayInProgress = isAuditDayInProgress(day.reportDate);
  if (!totals?.audit || !totals.pos || !totals.lotto || !totals.reconciliation_variance) {
    return [];
  }

  const availability = getAuditDataAvailability(day, { ...options, isDayInProgress });
  const payoutsExpected = totals.lotto.payouts_total + totals.lotto.scratcher_free_tickets_paid_amount;
  const lottoSalesVariance = availability.canShowLottoVariance ? totals.reconciliation_variance.lotto_sales_variance : null;
  const payoutVariance = availability.canShowLottoVariance ? totals.reconciliation_variance.payout_variance : null;
  const scratcherQtyVariance = availability.canShowScratcherVariance ? totals.reconciliation_variance.scratcher.quantity_variance : null;
  const scratcherRevenueVariance = availability.canShowScratcherVariance ? totals.reconciliation_variance.scratcher.revenue_variance : null;
  const canShowDayInProgressScratcherExpectedValues = isDayInProgress && totals.audit.audit_count > 0;

  const rows: AuditMetricRowData[] = [
    {
      id: `${day.reportDate}-daily-lotto`,
      label: "Lotto",
      expected: availability.hasLottoData ? formatCurrency(totals.lotto.sales) : "-",
      reported: availability.hasPosData ? formatCurrency(totals.pos.lotto_sales) : "-",
      variance: availability.canShowLottoVariance ? formatVarianceCurrency(lottoSalesVariance) : "-",
      tone: availability.canShowLottoVariance ? getAuditTone(lottoSalesVariance) : "progress",
    },
    {
      id: `${day.reportDate}-daily-payouts`,
      label: "Payouts",
      expected: availability.hasLottoData ? formatCurrency(payoutsExpected) : "-",
      reported: availability.hasPosData ? formatCurrency(totals.pos.payout) : "-",
      variance: availability.canShowLottoVariance ? formatVarianceCurrency(payoutVariance) : "-",
      tone: availability.canShowLottoVariance ? getAuditTone(payoutVariance) : "progress",
      dividerAfter: "strong",
    },
    {
      id: `${day.reportDate}-daily-scratcher-qty`,
      label: "Scratcher Qty",
      expected: availability.isAuditMissing ? "-" : formatNumber(totals.audit.total_sold),
      reported: availability.hasPosData ? formatNumber(totals.pos.scratcher_count) : "-",
      variance: availability.canShowScratcherVariance ? formatVarianceNumber(scratcherQtyVariance) : "-",
      tone: availability.canShowScratcherVariance ? getAuditTone(scratcherQtyVariance) : "progress",
    },
    {
      id: `${day.reportDate}-daily-scratcher-sales`,
      label: "Scratcher $",
      expected: availability.isAuditMissing ? "-" : formatCurrency(totals.audit.total_revenue),
      reported: availability.hasPosData ? formatCurrency(totals.pos.scratcher_sales) : "-",
      variance: availability.canShowScratcherVariance ? formatVarianceCurrency(scratcherRevenueVariance) : "-",
      tone: availability.canShowScratcherVariance ? getAuditTone(scratcherRevenueVariance) : "progress",
    },
  ];

  return isDayInProgress
    ? buildDayInProgressMetricRows(rows, {
        preserveScratcherExpectedValues: canShowDayInProgressScratcherExpectedValues,
      })
    : rows;
};

const getDailyAuditCardStatus = (
  day: StoreAuditSummaryRangeDayModel,
  options: { hasPosType: boolean; isLottoAuthorized: boolean }
) => {
  const isDayInProgress = isAuditDayInProgress(day.reportDate);
  const availability = getAuditDataAvailability(day, { ...options, isDayInProgress });
  const totalVariance = day.auditTotals?.reconciliation_variance?.total_variance;

  if (isDayInProgress) {
    return {
      label: "Day In Progress",
      tone: "progress" as const,
    };
  }

  if (availability.hasMissingSources) {
    return {
      label: "Needs",
      detail: availability.missingSources.join(" + "),
      tone: "progress" as const,
    };
  }

  const tone = getAuditTone(totalVariance);

  if (totalVariance == null) {
    return {
      label: "",
      tone,
    };
  }

  if (totalVariance === 0) {
    return {
      label: "Matched",
      tone,
    };
  }

  return {
    label: totalVariance > 0 ? "SURPLUS" : "DEFICIT",
    detail: formatSignedCurrency(totalVariance),
    tone,
  };
};

const hasReconciliationMismatch = (day: StoreAuditSummaryRangeDayModel) => {
  const variance = day.auditTotals?.reconciliation_variance;
  if (!variance) {
    return false;
  }

  return [
    variance.total_variance,
    variance.lotto_sales_variance,
    variance.payout_variance,
    variance.scratcher?.quantity_variance,
    variance.scratcher?.revenue_variance,
  ].some((value) => Number(value || 0) !== 0);
};

const getVerificationStatus = (day: StoreAuditSummaryRangeDayModel) => {
  const auditStatus = (day.verified as any)?.auditStatus || (day.verified as any)?.audit_status || day.verified?.status;
  if (auditStatus === "matched" || (!auditStatus && !hasReconciliationMismatch(day))) {
    return null;
  }

  if (auditStatus === "approved" || day.verified?.is_verified === true || day.verified?.status === "verified") {
    return {
      label: "Reconciled",
      color: "#047857",
      title: [day.verified?.user_name, day.verified?.date_time ? moment(day.verified.date_time).format("M/D/YY h:mm A") : ""]
        .filter(Boolean)
        .join(" | "),
    };
  }

  if (auditStatus === "pending_approval") {
    return {
      label: "Pending Approval",
      color: "#b45309",
      title: "",
    };
  }

  return {
    label: "Investigation Pending",
    color: auditDashboardTheme.colors.textNegative,
    title: "",
  };
};

export default function AuditDailyAuditGrid({
  storeId,
  days,
  selectedReportDate,
  sortDirection,
  onSortDirectionChange,
  onSelectReportDate,
  hasPosType,
  isLottoAuthorized,
  auditFilterOptions,
  filterFormMethods,
  onAuditFilterChange,
  scratcherAuditWindows,
  backstockLedger,
  manualSoldOutReportsByDate,
  manualSoldOutImpactsByDate,
  onVisibleReportDatesChange,
}: AuditDailyAuditGridProps) {
  const dayCardsContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!onVisibleReportDatesChange) {
      return;
    }

    if (!days.length) {
      onVisibleReportDatesChange([]);
      return;
    }

    const dayCards = Array.from(
      dayCardsContainerRef.current?.querySelectorAll<HTMLElement>("[data-audit-report-date]") ?? []
    );

    if (typeof IntersectionObserver === "undefined") {
      onVisibleReportDatesChange(days.map((day) => day.reportDate));
      return;
    }

    const visibleReportDates = new Set<string>();
    const observer = new IntersectionObserver((entries) => {
      let changed = false;

      entries.forEach((entry) => {
        const reportDate = (entry.target as HTMLElement).dataset.auditReportDate;
        if (!reportDate) {
          return;
        }

        if (entry.isIntersecting) {
          if (!visibleReportDates.has(reportDate)) {
            visibleReportDates.add(reportDate);
            changed = true;
          }
        } else if (visibleReportDates.delete(reportDate)) {
          changed = true;
        }
      });

      if (changed) {
        onVisibleReportDatesChange(Array.from(visibleReportDates));
      }
    }, { threshold: 0.01 });

    dayCards.forEach((dayCard) => observer.observe(dayCard));

    return () => {
      observer.disconnect();
    };
  }, [days, onVisibleReportDatesChange]);

  if (!days.length) {
    return null;
  }

  const handleDayCardClick = (reportDate: string) => {
    onSelectReportDate(reportDate);
  };

  const handleDayCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, reportDate: string) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelectReportDate(reportDate);
    }
  };

  return (
    <Box
      sx={{
        minWidth: 0,
        overflowX: "hidden",
        borderTop: "6px solid #f0f2f2",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(163, 174, 208, 0.42) transparent",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            pl: "12px",
            pr: "8px",
            py: "12px",
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderLeft: "4px solid",
            borderColor: auditDashboardTheme.colors.line,
            borderLeftColor: auditDashboardTheme.colors.blue,
            color: auditDashboardTheme.colors.text,
            bgcolor: auditDashboardTheme.colors.blueTint,
            fontSize: 17,
            fontWeight: 800,
            lineHeight: 1.2,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          <Typography component="h3" sx={{ m: 0, fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit" }}>
            Daily Audits
          </Typography>
          <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", minWidth: 0 }}>
            <Tooltip title={sortDirection === "asc" ? "Sort descending" : "Sort ascending"}>
              <IconButton
                size="small"
                onClick={() => onSortDirectionChange((current) => (current === "asc" ? "desc" : "asc"))}
                sx={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  p: "6px",
                  color: "inherit",
                  border: "1px solid",
                  borderColor: auditDashboardTheme.colors.line,
                  borderRadius: "8px",
                  bgcolor: auditDashboardTheme.colors.surface,
                  "&:hover": {
                    bgcolor: auditDashboardTheme.colors.blueSoft,
                  },
                }}
              >
                {sortDirection === "asc" ? <ArrowUpward sx={{ fontSize: 17 }} /> : <ArrowDownward sx={{ fontSize: 17 }} />}
              </IconButton>
            </Tooltip>
            <FormProvider {...filterFormMethods}>
              <Box
                sx={{
                  width: 112,
                  flexShrink: 0,
                  "& .MuiFormControl-root": {
                    height: "30px !important",
                  },
                  "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
                    boxSizing: "border-box",
                    height: "30px !important",
                    minHeight: "30px !important",
                    borderRadius: "8px",
                    bgcolor: auditDashboardTheme.colors.surface,
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "30px !important",
                    minHeight: "30px !important",
                    lineHeight: "30px !important",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
                    px: "10px !important",
                    py: "0 !important",
                    color: `${auditDashboardTheme.colors.text} !important`,
                    fontSize: "12px !important",
                    fontWeight: "500 !important",
                  },
                  "& .MuiSelect-icon": {
                    top: "50% !important",
                    transform: "translateY(-50%) !important",
                  },
                }}
              >
                <RHFSelect
                  name="auditListFilter"
                  options={auditFilterOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  defaultValue="all"
                  onChange={(event) => onAuditFilterChange(event.target.value as AuditListFilterKey)}
                  size="small"
                  fullWidth
                />
              </Box>
            </FormProvider>
          </Box>
        </Box>
        <Box ref={dayCardsContainerRef} sx={{ display: "grid", gap: "6px", p: "6px", bgcolor: "transparent" }}>
          {days.map((day) => {
            const auditCount = day.auditTotals?.audit?.audit_count ?? 0;
            const isActive = day.reportDate === selectedReportDate;
            const isDayInProgress = isAuditDayInProgress(day.reportDate);
            const status = getDailyAuditCardStatus(day, { hasPosType, isLottoAuthorized });
            const toneStyle = auditToneStyles[status.tone];
            const statusTextColor = status.label === "Needs" ? auditDashboardTheme.colors.textNegative : toneStyle.text;
            const rows = buildDailyAuditRows(day, { hasPosType, isLottoAuthorized });
            const verificationStatus = getVerificationStatus(day);
            const scratcherAuditWindowText = scratcherAuditWindows?.getWindowText(day.reportDate) ?? "-";
            const backstockDay = backstockLedger?.getDay(day.reportDate) ?? null;
            const backstockText = backstockDay
              ? `${pluralize(backstockDay.closingTickets, "ticket", "tickets")} | ${pluralize(backstockDay.closingPacks, "pack", "packs")}`
              : "-";
            const manualSoldOutReport = manualSoldOutReportsByDate?.get(day.reportDate);
            const hasManualSoldOutPacks = Boolean(manualSoldOutReport?.packs.length);
            const manualSoldOutText = manualSoldOutReport?.pack_count
              ? `${pluralize(manualSoldOutReport.total_tickets, "ticket", "tickets")} | ${pluralize(manualSoldOutReport.pack_count, "pack", "packs")} | ${formatCurrency(Number(manualSoldOutReport.total_retail_value))}`
              : "-";
            const manualSoldOutImpact = manualSoldOutImpactsByDate?.get(day.reportDate);
            const hasManualSoldOutImpact = Boolean(manualSoldOutImpact?.packs.length);
            const manualSoldOutImpactText = manualSoldOutImpact?.pack_count
              ? `${pluralize(manualSoldOutImpact.total_tickets, "ticket", "tickets")} | ${pluralize(manualSoldOutImpact.pack_count, "pack", "packs")} | ${formatCurrency(Number(manualSoldOutImpact.total_retail_value))}`
              : "-";

            return (
              <Box
                key={day.reportDate}
                component="div"
                role="button"
                data-audit-report-date={day.reportDate}
                tabIndex={0}
                aria-pressed={isActive}
                onClick={() => handleDayCardClick(day.reportDate)}
                onKeyDown={(event) => handleDayCardKeyDown(event, day.reportDate)}
                sx={{
                  appearance: "none",
                  position: "relative",
                  display: "block",
                  width: "100%",
                  minWidth: 0,
                  p: 0,
                  border: "1px solid",
                  borderColor: toneStyle.border,
                  borderRadius: "10px",
                  overflow: "hidden",
                  color: "inherit",
                  bgcolor: auditDashboardTheme.colors.surface,
                  cursor: "pointer",
                  font: "inherit",
                  textAlign: "left",
                  transition: "box-shadow 140ms ease, transform 140ms ease",
                  userSelect: "text",
                  "&::before": {
                    content: isActive ? '""' : "none",
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: 4,
                    bgcolor: auditDashboardTheme.colors.blue,
                    zIndex: 1,
                  },
                  "&:hover": {
                    boxShadow: "0 8px 18px rgba(34, 43, 69, 0.08)",
                  },
                  "&:focus-visible": {
                    outline: "2px solid rgba(44, 108, 160, 0.28)",
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: "10px",
                    px: "16px",
                    py: "12px",
                    bgcolor: toneStyle.bg,
                    borderBottom: "1px solid",
                    borderColor: auditDashboardTheme.colors.lineSoft,
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      color: auditDashboardTheme.colors.text,
                      fontSize: 17,
                      fontWeight: 700,
                      lineHeight: 1.15,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box component="span">{moment(day.reportDate).format("ddd, M/D/YYYY")}</Box>
                    <Box component="span" sx={{ color: "#222b4599" }}>
                      {` \u2022 ${auditCount} ${auditCount === 1 ? "audit" : "audits"}`}
                    </Box>
                  </Typography>
                  {status.label ? (
                    <Box sx={{ minWidth: 0, textAlign: "right" }}>
                      <Typography
                        component="div"
                        sx={{
                          color: statusTextColor,
                          fontSize: 13,
                          lineHeight: 1.15,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 14, fontWeight: 800 }}>
                          {status.label}
                        </Box>
                        {status.detail ? (
                          <Box component="span" sx={{ fontSize: 13, fontWeight: status.label === "Needs" ? 500 : 700 }}>
                            {` ${status.detail}`}
                          </Box>
                        ) : null}
                      </Typography>
                      {verificationStatus ? (
                        <Tooltip title={verificationStatus.title}>
                          <Typography
                            component="div"
                            sx={{
                              mt: "3px",
                              color: verificationStatus.color,
                              fontSize: 11,
                              fontWeight: 500,
                              lineHeight: 1.1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {verificationStatus.label}
                          </Typography>
                        </Tooltip>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>

                <Box sx={{ px: "12px", pt: "10px", pb: "8px", minWidth: 0 }}>
                  {rows.length ? (
                    <AuditSummaryComparisonTable rows={rows} variant="range" />
                  ) : (
                    <Typography component="div" sx={{ py: 2, color: auditDashboardTheme.colors.textMutedStrong, fontSize: 12, textAlign: "center" }}>
                      No summary data found.
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr)",
                      gap: "4px",
                      alignItems: "start",
                      mt: "7px",
                      pt: "7px",
                      borderTop: "1px solid",
                      borderColor: auditDashboardTheme.colors.lineSoft,
                    }}
                  >
                    {hasManualSoldOutPacks ? (
                      <Box
                        aria-label="Manual sold out summary"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: 1,
                          alignItems: "center",
                          px: "8px",
                          py: "6px",
                          border: "1px solid rgba(180, 83, 9, 0.18)",
                          borderRadius: "8px",
                          bgcolor: "rgba(255, 247, 237, 0.65)",
                        }}
                      >
                        <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                          Manual Sold Out
                        </Typography>
                        <Typography sx={{ color: "#b45309", fontSize: 12, fontWeight: 500, lineHeight: 1.3, whiteSpace: "nowrap", textAlign: "right" }}>
                          {manualSoldOutText}
                        </Typography>
                      </Box>
                    ) : null}
                    {hasManualSoldOutImpact ? (
                      <Box
                        aria-label="Later manual sold out impact summary"
                        sx={{
                          display: "grid",
                          gap: "4px",
                          px: "8px",
                          py: "6px",
                          border: "1px solid rgba(44, 108, 160, 0.22)",
                          borderRadius: "8px",
                          bgcolor: "rgba(239, 246, 255, 0.72)",
                        }}
                      >
                        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 1, alignItems: "center" }}>
                          <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                            Audit Updated Later
                          </Typography>
                          <Typography sx={{ color: auditDashboardTheme.colors.blue, fontSize: 12, fontWeight: 500, lineHeight: 1.3, whiteSpace: "nowrap", textAlign: "right" }}>
                            {manualSoldOutImpactText}
                          </Typography>
                        </Box>
                        {manualSoldOutImpact?.packs.map((pack) => (
                          <Typography
                            key={pack.audit_detail_id}
                            component="div"
                            sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 11, fontWeight: 400, lineHeight: 1.3 }}
                          >
                            <Box
                              component={Link}
                              to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle`}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              sx={{ color: auditDashboardTheme.colors.blue, textDecoration: "underline", textUnderlineOffset: "2px" }}
                            >
                              {pack.game_id}
                            </Box>
                            <Box component="span">-</Box>
                            <Box
                              component={Link}
                              to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle/${pack.pack_id}?sold_out=true`}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              sx={{ color: auditDashboardTheme.colors.blue, textDecoration: "underline", textUnderlineOffset: "2px" }}
                            >
                              {pack.pack_id}
                            </Box>
                            {` • ${pluralize(pack.tickets, "ticket", "tickets")} • ${formatCurrency(Number(pack.retail_value))} • Manual Sold Out `}
                            <Box
                              component={Link}
                              to={`/store/${storeId}?from=${pack.marked_date}&days=1&report_date=${pack.marked_date}`}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              sx={{ color: auditDashboardTheme.colors.blue, textDecoration: "underline", textUnderlineOffset: "2px" }}
                            >
                              {moment(pack.marked_date).format("M/D/YY")}
                            </Box>
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: "12px",
                        alignItems: "start",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography component="div" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 12, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                          Scratcher Audit Window
                        </Typography>
                        <Typography
                          component="div"
                          sx={{
                            mt: "2px",
                            color: auditDashboardTheme.colors.textMutedStrong,
                            fontSize: 12,
                            fontWeight: 400,
                            fontStyle: "italic",
                            lineHeight: 1.3,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {scratcherAuditWindowText}
                        </Typography>
                      </Box>
                      <AuditDashboardBackstockLink
                        reportDate={day.reportDate}
                        sx={{ alignItems: "flex-start", textAlign: "right" }}
                      >
                        <Box>
                          <Typography component="div" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 12, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                            Live Backstock
                          </Typography>
                          <Typography component="div" sx={{ mt: "2px", color: "#df9400", fontSize: 12, fontWeight: 500, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                            {backstockText}
                          </Typography>
                        </Box>
                      </AuditDashboardBackstockLink>
                    </Box>
                  </Box>
                  {isDayInProgress ? (
                    <Typography
                      component="div"
                      sx={{
                        mt: "7px",
                        mx: "-12px",
                        mb: "-8px",
                        px: "12px",
                        pt: "7px",
                        pb: "8px",
                        borderTop: "1px solid",
                        borderColor: auditDashboardTheme.colors.lineSoft,
                        bgcolor: toneStyle.bg,
                        color: auditDashboardTheme.colors.textProgress,
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: 1.3,
                      }}
                    >
                      Discrepancy is not final until the day close
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
